// CR151: Eligibility Scope Rules Editor component
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, Plus, Trash2, GripVertical } from "lucide-react";
import type { EligibilityScopeRule, EligibilityScope, SourceType } from "@/lib/deal-policy/types";

interface EligibilityRulesEditorProps {
  rules: EligibilityScopeRule[];
  onChange: (rules: EligibilityScopeRule[]) => void;
}

const SCOPE_OPTIONS: EligibilityScope[] = [
  "CONSUMER", "CORPORATE", "MEMBERS_ONLY", "STUDENT", "EMPLOYEE", "NEW_CUSTOMER", "UNKNOWN"
];

const SOURCE_OPTIONS: SourceType[] = ["WEB", "FLYER", "FORUM"];

const SCOPE_COLORS: Record<EligibilityScope, string> = {
  CONSUMER: "bg-green-100 text-green-800",
  CORPORATE: "bg-blue-100 text-blue-800",
  MEMBERS_ONLY: "bg-purple-100 text-purple-800",
  STUDENT: "bg-yellow-100 text-yellow-800",
  EMPLOYEE: "bg-orange-100 text-orange-800",
  NEW_CUSTOMER: "bg-pink-100 text-pink-800",
  UNKNOWN: "bg-gray-100 text-gray-800",
};

export function EligibilityRulesEditor({ rules, onChange }: EligibilityRulesEditorProps) {
  const [expandedRule, setExpandedRule] = useState<string | null>(null);

  const updateRule = (id: string, updates: Partial<EligibilityScopeRule>) => {
    onChange(rules.map(r => r.id === id ? { ...r, ...updates } : r));
  };

  const deleteRule = (id: string) => {
    onChange(rules.filter(r => r.id !== id));
  };

  const addRule = () => {
    const newRule: EligibilityScopeRule = {
      id: `rule_${Date.now()}`,
      name: "New Rule",
      scope: "UNKNOWN",
      enabled: true,
      priority: 50,
      matchers: {
        keywords: [],
        keyword_mode: "ANY",
        regex_patterns: [],
        negative_keywords: [],
      },
      conditions: {
        min_threshold_cad: null,
        requires_threshold_present: false,
        applies_to_sources: ["WEB", "FLYER", "FORUM"],
      },
    };
    onChange([...rules, newRule]);
    setExpandedRule(newRule.id);
  };

  const sortedRules = [...rules].sort((a, b) => b.priority - a.priority);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          Rules are evaluated by priority (highest first). First matching rule sets the eligibility scope.
        </p>
        <Button size="sm" variant="outline" onClick={addRule}>
          <Plus className="h-4 w-4 mr-1" />
          Add Rule
        </Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">On</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-32">Scope</TableHead>
              <TableHead className="w-20">Priority</TableHead>
              <TableHead className="w-24">Sources</TableHead>
              <TableHead className="w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedRules.map((rule) => (
              <Collapsible key={rule.id} open={expandedRule === rule.id}>
                <TableRow 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setExpandedRule(expandedRule === rule.id ? null : rule.id)}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Switch
                      checked={rule.enabled}
                      onCheckedChange={(checked) => updateRule(rule.id, { enabled: checked })}
                    />
                  </TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {expandedRule === rule.id ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                      {rule.name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={SCOPE_COLORS[rule.scope]}>{rule.scope}</Badge>
                  </TableCell>
                  <TableCell>{rule.priority}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {rule.conditions.applies_to_sources.map(s => (
                        <Badge key={s} variant="outline" className="text-xs">
                          {s}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => deleteRule(rule.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
                <CollapsibleContent asChild>
                  <TableRow>
                    <TableCell colSpan={6} className="p-0">
                      <div className="p-4 bg-muted/30 space-y-4">
                        <div className="grid grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label>Rule Name</Label>
                            <Input
                              value={rule.name}
                              onChange={(e) => updateRule(rule.id, { name: e.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Target Scope</Label>
                            <Select
                              value={rule.scope}
                              onValueChange={(v) => updateRule(rule.id, { scope: v as EligibilityScope })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {SCOPE_OPTIONS.map(s => (
                                  <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Priority (higher = first)</Label>
                            <Input
                              type="number"
                              value={rule.priority}
                              onChange={(e) => updateRule(rule.id, { priority: parseInt(e.target.value) || 0 })}
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Keywords (one per line)</Label>
                            <Textarea
                              rows={4}
                              value={rule.matchers.keywords.join("\n")}
                              onChange={(e) => updateRule(rule.id, {
                                matchers: {
                                  ...rule.matchers,
                                  keywords: e.target.value.split("\n").filter(Boolean)
                                }
                              })}
                              placeholder="corporate&#10;bulk&#10;business"
                            />
                            <div className="flex gap-2 mt-1">
                              <Label className="text-xs">Mode:</Label>
                              <Select
                                value={rule.matchers.keyword_mode}
                                onValueChange={(v) => updateRule(rule.id, {
                                  matchers: { ...rule.matchers, keyword_mode: v as "ANY" | "ALL" }
                                })}
                              >
                                <SelectTrigger className="h-7 w-20">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="ANY">ANY</SelectItem>
                                  <SelectItem value="ALL">ALL</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Regex Patterns (one per line)</Label>
                            <Textarea
                              rows={4}
                              value={rule.matchers.regex_patterns.join("\n")}
                              onChange={(e) => updateRule(rule.id, {
                                matchers: {
                                  ...rule.matchers,
                                  regex_patterns: e.target.value.split("\n").filter(Boolean)
                                }
                              })}
                              placeholder="corporate\s*gift&#10;bulk\s*order"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label>Min Threshold (CAD)</Label>
                            <Input
                              type="number"
                              value={rule.conditions.min_threshold_cad ?? ""}
                              onChange={(e) => updateRule(rule.id, {
                                conditions: {
                                  ...rule.conditions,
                                  min_threshold_cad: e.target.value ? parseFloat(e.target.value) : null
                                }
                              })}
                              placeholder="500"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Requires Threshold?</Label>
                            <div className="flex items-center gap-2 h-10">
                              <Switch
                                checked={rule.conditions.requires_threshold_present}
                                onCheckedChange={(checked) => updateRule(rule.id, {
                                  conditions: { ...rule.conditions, requires_threshold_present: checked }
                                })}
                              />
                              <span className="text-sm text-muted-foreground">
                                {rule.conditions.requires_threshold_present ? "Yes" : "No"}
                              </span>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Negative Keywords</Label>
                            <Textarea
                              rows={2}
                              value={rule.matchers.negative_keywords.join("\n")}
                              onChange={(e) => updateRule(rule.id, {
                                matchers: {
                                  ...rule.matchers,
                                  negative_keywords: e.target.value.split("\n").filter(Boolean)
                                }
                              })}
                              placeholder="exclude term"
                            />
                          </div>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                </CollapsibleContent>
              </Collapsible>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
